import { computed, type Ref, ref, useSlots, watch, watchEffect } from 'vue';

/** The props every select-shaped input carries into the selection logic. */
export type SelectSelectionProps<T extends string> = {
  options: T[];
  multiple?: boolean;
  optionText?: (option: T) => string;
};

/** `[null]` is the wire form of "nothing selected"; `[]` is the internal one. */
function arrayEq<T extends string>(a: T[] | [null], b: T[] | [null]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  if (a.length === 0) {
    return true;
  }
  return a.every((v, i) => v === b[i]);
}

/**
 * Everything a select-shaped input does with its value, independent of how it
 * renders its trigger: the shadow copy the dropdown edits, the lazy/eager emit,
 * toggling an option, and the plain-text label an option carries.
 *
 * Shared by `NxSelectInput` (non-editable trigger) and the filterable variant
 * (real input) so the two cannot drift apart — a bug fixed in one is fixed in
 * both, which is why this is not duplicated per component.
 *
 * `props` must be the component's own props object so it stays reactive.
 */
export function useSelectSelection<T extends string>(
  props: SelectSelectionProps<T>,
  model: Ref<T[] | [null]>,
  modifiers: { lazy?: boolean }
) {
  const shadowValue = ref(arrayEq(model.value, [null]) ? [] : (model.value as T[])) as Ref<T[]>;
  watch(model, (value) => (shadowValue.value = arrayEq(value, [null]) ? [] : (value as T[])));

  // A computed, not a map built once at setup: it decides the order of a
  // multi-select value, and `options` can change at runtime (a reloaded column
  // config, a narrowed enum list) — a stale map sorts against positions that no
  // longer exist.
  const optionsToIndex = computed(() => new Map(props.options.map((v, i) => [v, i])));
  function sortByIndex(a: T, b: T) {
    // A held value can be absent from `options` — an async load, a reloaded column
    // config, a narrowed enum list, or simply a legacy value the list no longer
    // offers. Subtracting a missing index yields NaN, and `sort` with an
    // inconsistent comparator is implementation-defined, so the persisted order
    // would depend on the engine. Unknown values sort last, deterministically.
    const indexA = optionsToIndex.value.get(a) ?? Number.MAX_SAFE_INTEGER;
    const indexB = optionsToIndex.value.get(b) ?? Number.MAX_SAFE_INTEGER;
    return indexA - indexB;
  }

  function labelOf(option: T): string {
    return props.optionText ? props.optionText(option) : String(option);
  }

  // A custom `#option` slot means the visible text is not the raw value, and
  // without `optionText` everything derived from it — the filter, the typeahead,
  // the tooltip — matches text the user cannot read. Warned here rather than in
  // one trigger, so both selects surface it; `NxSelectInput` stayed silent about
  // its own consumers while only the filter variant complained.
  if (import.meta.env.DEV) {
    const slots = useSlots();
    watchEffect(() => {
      if (slots.option && !props.optionText) {
        console.warn(
          '[nx-ui] a select with a custom `#option` slot but no `optionText`: filtering, typeahead and the tooltip match the raw option values, not the text the slot renders.'
        );
      }
    });
  }

  const optionTexts = computed(() => props.options.map(labelOf));

  /**
   * Native tooltip for the trigger, which clips its text with an ellipsis —
   * without it a long or multi-select value is only readable by opening the
   * dropdown. `undefined` while empty: the placeholder is fully visible and needs
   * no tooltip. Reads `optionText`, so a consumer rendering translated labels
   * through the `#option` slot gets those here rather than the raw values.
   */
  const triggerTitle = computed(() =>
    shadowValue.value.length > 0 ? shadowValue.value.map(labelOf).join(', ') : undefined
  );

  /** The selection as plain text — what a trigger shows when nothing is typed. */
  const selectionText = computed(() => shadowValue.value.map(labelOf).join(', '));

  function commit() {
    if (!arrayEq(model.value, shadowValue.value)) {
      model.value = shadowValue.value.length ? shadowValue.value : [null];
    }
  }

  function emitIfNotLazy() {
    if (modifiers.lazy) return;
    commit();
  }

  /** Emit a pending lazy value. Call when the dropdown closes. */
  function commitIfLazy() {
    if (modifiers.lazy) commit();
  }

  /**
   * Add or remove an option.
   *
   * @returns whether the dropdown should close — single-select is done after one
   *   pick, multi-select stays open for the next one.
   */
  function toggleOption(option: T): boolean {
    if (props.multiple) {
      const idx = shadowValue.value.indexOf(option);
      if (idx >= 0) {
        shadowValue.value = shadowValue.value.filter((v) => v !== option);
      } else {
        shadowValue.value = [...shadowValue.value, option].sort(sortByIndex);
      }
      emitIfNotLazy();
      return false;
    }

    shadowValue.value = [option];
    emitIfNotLazy();
    return true;
  }

  /** Clear the value by picking the placeholder row. Always closes. */
  function selectPlaceholder() {
    shadowValue.value = [];
    emitIfNotLazy();
  }

  /**
   * Throw away edits that were never committed and fall back to the model.
   *
   * Only a lazy model can have any: an eager one emitted on every pick, and the
   * `model` watcher has already written those back into the shadow copy. This is
   * what lets Escape mean "cancel" on a lazy field instead of committing the very
   * picks the user is backing out of.
   */
  function revert() {
    shadowValue.value = arrayEq(model.value, [null]) ? [] : (model.value as T[]);
  }

  return {
    shadowValue,
    labelOf,
    optionTexts,
    triggerTitle,
    selectionText,
    toggleOption,
    selectPlaceholder,
    commitIfLazy,
    revert,
  };
}
